import { useCallback, useState } from "react";
import { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useDebounce } from "../utils/hooks/useDebounce";
import ReprintRequestFilter from "../components/ReprintRequest/Filter";
import ReprintRequestTable from "../components/ReprintRequest/Table";
import { CHARCOAL } from "../components/ReprintRequest/data";
import { findNumberSetting, useSystemSettings } from "../utils/hooks/useSystemSettings";
import { SYSTEM_SETTING_KEY } from "../types/system-setting.type";

export default function ReprintRequestPage() {
  // Default to the inbox view: the approver's job here is the PENDING queue.
  const [status, setStatus] = useState<string>("PENDING");
  const [search, setSearch] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [triggerFetch, setTriggerFetch] = useState<number>(0);
  const [updatedAt, setUpdatedAt] = useState<string>("");

  const debouncedSearch = useDebounce(search, 500);

  // How long an approval opens reprinting for — the `reprint.request_valid_hours` setting.
  const { settings } = useSystemSettings();
  const grantValidHours = findNumberSetting(settings, SYSTEM_SETTING_KEY.reprintRequestValidHours);

  const refresh = useCallback(() => setTriggerFetch((prev) => prev + 1), []);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" sx={{ color: CHARCOAL, fontWeight: 700 }}>
          คำขอพิมพ์ซ้ำ (Reprint Requests)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ตรวจสอบและอนุมัติคำขอพิมพ์บิลซ้ำที่ส่งมาจากพนักงานขาย
        </Typography>
      </Box>

      {grantValidHours !== undefined && (
        <Alert severity="info" sx={{ mb: 2 }}>
          เมื่ออนุมัติ ระบบจะเปิดสิทธิ์พิมพ์ซ้ำให้พนักงานขายเป็นเวลา {grantValidHours} ชั่วโมง
        </Alert>
      )}

      <Paper sx={{ padding: "24px 16px 16px", mb: 2 }}>
        <ReprintRequestFilter
          search={search}
          setSearch={setSearch}
          status={status}
          setStatus={setStatus}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
        />
      </Paper>

      <ReprintRequestTable
        search={debouncedSearch}
        status={status}
        dateFrom={dateFrom}
        dateTo={dateTo}
        triggerFetch={triggerFetch}
        onRefresh={refresh}
        updatedAt={updatedAt}
        setUpdatedAt={setUpdatedAt}
      />
    </Box>
  );
}
